import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../../../context/Toast';
import {
  MyTimeTrackingDocument,
  MyTimeTrackingQuery,
} from '../../__generated__/graphql';
import { useClockOutRequirements } from './useClockOutRequirements';
import { useProjectsShowTrackedTime } from './useProjectsShowTrackedTime';

export const useClockOutGuard = () => {
  const { t } = useTranslation();
  const { addError } = useToasts();
  const client = useApolloClient();
  const { isTimeTrackingWithProjects } = useProjectsShowTrackedTime();
  const { requireProjectOnClockOut } = useClockOutRequirements();

  return useCallback((): boolean => {
    if (!isTimeTrackingWithProjects || !requireProjectOnClockOut) {
      return true;
    }
    // Read the cache at call time so a recheck after the project save settles
    // sees the persisted value, not a stale render-time snapshot.
    const data = client.readQuery<MyTimeTrackingQuery>({
      query: MyTimeTrackingDocument,
    });
    const hasProject =
      data?.me.currentTimeEntry?.flair__Tracking_Project__c != null;
    if (!hasProject) {
      addError(t('timeTracking.current.projectRequiredOnClockOut'));
      return false;
    }
    return true;
  }, [
    client,
    isTimeTrackingWithProjects,
    requireProjectOnClockOut,
    addError,
    t,
  ]);
};
